import Box from "@ui-kit/layout/Box/Box.web";
import ReadMore from "@ui-kit/typography/ReadMore/ReadMore.web";
import useWindowSize from "@shared/hooks/useWindowSize";
import ItemPasswordEntry from "@shared/modules/events/components/ItemPasswordEntry/ItemPasswordEntry.web";
import { useGetEventPath } from "@shared/modules/events/hooks/useGetEventPath";
import PaymentVerbiage from "@shared/modules/ticket-types/components/PaymentVerbiage/PaymentVerbiage.web";
import {
  isTicketTypeWaitlisted,
  isOnSaleTicketType,
} from "@shared/modules/ticket-types/utils/ticket-type-utils";
import breakpoints from "@shared/theme/props/breakpoints";
import { useTranslation } from "@shared/utils/internationalization";
import { useMemo } from "react";
import { useSeatingState } from "../../contexts/useSeatingState";
import { ItemSelectVerbiageProps } from "./ItemSelectVerbiage.props";
import { useItemSelectVerbiage } from "./useItemSelectVerbiage";

const { sm } = breakpoints;

const ItemSelectVerbiage = (props: ItemSelectVerbiageProps) => {
  const {
    item,
    venueData,
    voucherData,
    ticketAuthArray,
    updateTicketAuthArray,
  } = props;

  const { t } = useTranslation("seating");
  const { seatingEventData } = useSeatingState();
  const { getEventPath } = useGetEventPath();
  const { width: windowWidth } = useWindowSize();

  const isSmallerThanSm = useMemo(
    () => Boolean(windowWidth && windowWidth < sm - 1),
    [windowWidth]
  );

  const { isSoldOut, hasPaymentPlans, showPaymentVerbiage, showPasswordInput } =
    useItemSelectVerbiage(props);

  return (
    <Box opacity={isSoldOut && !isTicketTypeWaitlisted(item) ? "0.3" : "1.0"}>
      {/* Show payment verbiage for item vouchers */}
      {showPaymentVerbiage && seatingEventData && (
        <PaymentVerbiage
          hasPaymentPlan={hasPaymentPlans}
          eventStartsOn={seatingEventData.starts_on ?? undefined}
          eventEndsOn={seatingEventData.ends_on ?? undefined}
          customStartsOn={item.custom_starts_on ?? undefined}
          customEndsOn={item.custom_ends_on ?? undefined}
          timezone={seatingEventData.timezone}
          ticketVoucherData={{
            type: item,
            basketVoucherCode: voucherData.basketVoucherCode,
            userVouchers: voucherData.userVouchers,
            eventUrl: `${getEventPath(seatingEventData)}seating/`,
            venueSlug: venueData?.slug ?? "",
            venueName: venueData?.name,
          }}
          isOnSale={isOnSaleTicketType(item)}
        />
      )}

      {/* item password input */}
      {showPasswordInput && (
        <ItemPasswordEntry
          item={item}
          slugToCheck={seatingEventData?.slug}
          authArray={ticketAuthArray}
          updateAuthArray={updateTicketAuthArray}
        />
      )}

      <Box
        hidden={!isSmallerThanSm || !item.description}
        minHeight="element.lg"
        mt="none"
      />

      {/* Show item description */}
      {item.description && (
        <ReadMore
          descriptionHtml={item.description}
          openReadMoreText={t("Read More", { ns: "common" })}
          closeReadMoreText={t("Show Less", { ns: "common" })}
        />
      )}
    </Box>
  );
};

export default ItemSelectVerbiage;
